"use client";

import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import { useTenancy } from "@/contexts/TenancyContext";
import type { User as TenancyUser } from "@/contexts/TenancyContext";

interface MultiUserComboboxProps {
  selected: string[];
  setSelected: (values: string[]) => void;
  placeholder?: string;
  className?: string;
  onUserSelect?: (userId: string) => void;
  onUserDeSelect?: (userId: string) => void;
  onAllSelect?: () => void;
  onAllDeselect?: (userList: TenancyUser[]) => void;
}

export const MultiUserCombobox: React.FC<MultiUserComboboxProps> = ({ selected, setSelected, placeholder, className, onUserSelect, onUserDeSelect, onAllSelect, onAllDeselect }) => {
  const [open, setOpen] = React.useState(false);
  const { users, userIdToName } = useTenancy();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  // const { users, loading, error, refresh } = useUsersList();

  console.log('[MultiUserCombobox]', { users, selected, loading, error });

  // Ensure selected is always a valid array
  const safeSelected = Array.isArray(selected) ? selected : [];

  const availableUsers = React.useMemo<TenancyUser[]>(() => {
    const baseUsers = Array.isArray(users) ? [...users] : [];
    if (!safeSelected.length) {
      return baseUsers;
    }

    const missingIds = safeSelected.filter(id => !baseUsers.some(user => user.id === id));
    if (!missingIds.length) {
      return baseUsers;
    }

    const augmented = [...baseUsers];
    missingIds.forEach(id => {
      if (augmented.some(user => user.id === id)) {
        return;
      }
      const displayName = userIdToName?.[id] || id;
      const parts = displayName.split(/\s+/).filter(Boolean);
      const firstName = parts.shift() || displayName;
      const lastName = parts.join(' ');
      const fallbackUser: TenancyUser = {
        id,
        firstName,
        lastName,
        email: '',
        roles: {},
      };
      augmented.push(fallbackUser);
    });

    return augmented;
  }, [users, safeSelected, userIdToName]);

  const formatUserLabel = React.useCallback((user: TenancyUser) => {
    const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
    if (user.email) {
      return `${name} <${user.email}>`.trim();
    }
    return name || userIdToName?.[user.id] || user.id;
  }, [userIdToName]);

  const handleSelect = React.useCallback((userId: string) => {
    console.log('[MultiUserCombobox]', 'handleSelect', { userId, safeSelected });
    // Create a new array based on current selection
    if (safeSelected.includes(userId)) {
      // If already selected, remove it
      setSelected(safeSelected.filter(v => v !== userId));
      if (onUserDeSelect) onUserDeSelect(userId);
    } else {
      // Otherwise add it
      setSelected([...safeSelected, userId]);
      if (onUserSelect) onUserSelect(userId);
    }

  }, [safeSelected, setSelected, onUserDeSelect, onUserSelect]);

  const handleSelectAll = React.useCallback(() => {
    // Check if all available users are currently selected
    const allSelected = availableUsers.length > 0 &&
      availableUsers.every(user => safeSelected.includes(user.id));

    if (allSelected) {
      // If all are selected, deselect all
      onAllDeselect?.(availableUsers);
      setSelected([]);
    } else {
      // Otherwise, select all
      onAllSelect?.();
      setSelected(availableUsers.map(user => user.id));
    }
  }, [availableUsers, safeSelected, setSelected, onAllDeselect, onAllSelect]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className={cn("w-full", className)}>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full min-w-0 justify-between"
            type="button"
            disabled={loading}
          >
            {safeSelected.length > 0
              ? (
                <div className="truncate">
                  {`${safeSelected.length} user${safeSelected.length > 1 ? 's' : ''} selected`}
                </div>
              )
              : (placeholder || "Select users...")}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </div>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[--radix-popover-trigger-width] min-w-0 p-0"
      >
        <Command
          filter={(value, search) => {
            // Always allow 'Select All' to match
            if (value === 'select-all-option') return 1;
            // Find the user for this CommandItem
            const trimmedValue = value.trim();
            const user = availableUsers.find(
              u => formatUserLabel(u).trim() === trimmedValue
            );
            if (!user) return 0;
            const searchValue = search.trim().toLowerCase();
            if (!searchValue) return 1;
            if (searchValue.includes('@')) {
              // Email search: exact or prefix match, case-insensitive
              return (user.email ?? '').toLowerCase().startsWith(searchValue) ? 1 : 0;
            }
            const nameMatch = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.toLowerCase();
            const labelMatch = formatUserLabel(user).toLowerCase();
            return nameMatch.includes(searchValue) || labelMatch.includes(searchValue) ? 1 : 0;
          }}
        >
          <CommandInput placeholder="Search users..." disabled={loading} />
          <CommandList className="max-h-[400px] overflow-auto" onWheel={e => e.stopPropagation()}>
            <CommandEmpty>No users found.</CommandEmpty>
            <CommandGroup>
              {/* Select All option */}
              <CommandItem
                value="select-all-option"
                onSelect={handleSelectAll}
                className="font-medium border-b"
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    availableUsers.length > 0 && availableUsers.every(user => safeSelected.includes(user.id)) ? "opacity-100" : "opacity-0"
                  )}
                />
                Select All
              </CommandItem>

              {loading ? (
                <CommandEmpty>Loading users...</CommandEmpty>
              ) : (
                availableUsers.map((user) => (
                  <CommandItem
                    key={user.id}
                    value={formatUserLabel(user)}
                    onSelect={(value) => {
                      // Prevent the popover from closing by stopping event propagation
                      handleSelect(user.id);
                      // Keep the popover open
                      setOpen(true);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        safeSelected.includes(user.id) ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {formatUserLabel(user)}
                  </CommandItem>
                ))
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
